import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  TECH_FLAG_KINDS,
  TECH_FLAG_KIND_LABEL,
  type TechFlagKind,
} from '@servwell/shared';
import { useTRPC } from '../../lib/trpc';
import { useDraft, clearLandedDraft } from '../../lib/useDraft';
import { draftKey } from '../../lib/drafts';
import { uploadQueue, useQueuedPhotos } from '../../lib/upload-queue';
import { failedWriteLog, useFailedWriteFor } from '../../lib/failed-writes';
import { writesFor } from '../../lib/pending-writes';
import {
  EMPTY_FLAG_DRAFT,
  flagKindOf,
  giveBackUnlessKept,
  type FlagDraft,
} from '../../lib/ticket-photos';
import { Button, bigButton } from '../ui/Button';
import { Notice } from '../ui/Badge';
import { Sheet } from '../ui/Sheet';
import { mutationState, refreshUntilShown } from '../ui/QueryStates';
import {
  CHECKING_LABEL,
  useQueueRead,
  useQueuedWrite,
  useUnsentWrite,
} from '../ui/useConnection';
import { useTapOnce } from '../ui/useTapOnce';
import { PhotoGrid } from '../photos/PhotoGrid';
import { sendOnce } from '../tickets/sendOnce';

const FIELD =
  'w-full rounded-[var(--radius)] border border-border bg-surface px-2 text-sm disabled:opacity-60';

/**
 * "Flag a problem" (ops insights Phase 5a, ticket 03): a tech on site tells the office about
 * something that went wrong, with a few words and photos, with or without signal.
 *
 * ⭐ It works offline like "Leave a note": `ticket.flag` is replayable, queued on the phone
 * with its own key, and the draft (title, what's wrong, the ticket id) survives the tab being
 * reclaimed. The ticket id is minted on the phone at the first keystroke or photo, so an
 * untouched form keeps no draft. Photos taken in the sheet are PARKED on the phone (never
 * sent) until the flag is; then the upload queue holds them behind it until the ticket exists
 * (`lib/ticket-photos.ts`).
 *
 * ⭐ What kind of problem (Phase 6a §3): four choices, Other already chosen, so a flag is still
 * one tap. The kind is part of the draft and of the flag's variables, so it survives a reload,
 * a queued send, and a flag given back (`giveFlagBack` reads it off the variables).
 *
 * ⭐ Several flags per stop, offline too: Send clears the draft at once (the queued write
 * carries everything it needs) and the next keystroke mints a new id. A flag that will not be
 * delivered from this phone comes BACK to the draft with its photos: refused before it was
 * kept (here, `giveBackUnlessKept`), or dismissed from the changes panel (`startTicketPhotos`).
 *
 * 🔴 One flag per tap. The flag goes out ON the tap, so `disabled` cannot stop a double tap
 * (LESSONS 2026-10-02): the shared lock (`useTapOnce`) is taken synchronously, before the key
 * is minted, so a second tap in the same render (the same draft) is ignored. It is released
 * when the draft it was taken for is gone, i.e. when the draft's ticket id changes: a flag
 * that is sent clears the draft (the next problem gets a new id, offline too, where the flag
 * waits unsettled), and a flag that comes back to the draft brings its id back. Minting the
 * key throwing releases it at once (`useTapOnce`).
 */
export function FlagProblem({ visitId, userId }: { visitId: string; userId: string }) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [draft, setDraft, clearDraft] = useDraft<FlagDraft>(
    { kind: 'ticket-flag', userId, id: visitId },
    EMPTY_FLAG_DRAFT,
  );
  /**
   * The id the NEXT draft will take, at its first keystroke or photo. The photo grid needs an
   * id before anything is typed; a photo taken under it writes it into the draft (below).
   */
  const [nextId, setNextId] = useState(() => crypto.randomUUID());
  const ticketId = draft.ticketId || nextId;
  // A draft kept before the kind existed has none: Other.
  const kind = flagKindOf(draft.category);
  const withId = (d: FlagDraft): FlagDraft =>
    d.ticketId ? d : { ...d, ticketId: nextId };
  const photosHere = useQueuedPhotos('ticket', ticketId).length > 0;
  useEffect(() => {
    if (photosHere && !draft.ticketId) {
      setDraft((d) => (d.ticketId ? d : { ...d, ticketId: nextId }));
    }
  }, [photosHere, draft.ticketId, nextId, setDraft]);

  const meta = { draftKey: draftKey('ticket-flag', userId, visitId) };
  // From the QUEUE, not this observer: this draft's flag still on its way (a draft that came
  // back from the disk after its flag was sent) holds the form.
  const unsent = useUnsentWrite('ticket.flag', draft.ticketId || undefined, 'ticketId');
  // Refused, it is retried from Changes not sent with its ORIGINAL key; a second send of the
  // same ticket id here could only be refused too.
  const refused = useFailedWriteFor(
    'ticket.flag',
    draft.ticketId || undefined,
    'ticketId',
  );
  // The page button: a flag from this stop saved for want of a signal.
  const savedForLater = useQueuedWrite('ticket.flag', visitId, 'visitId');
  const checking = !useQueueRead();
  const lock = useTapOnce();
  useEffect(() => lock.release(), [lock, draft.ticketId]);

  const flag = useMutation(
    trpc.ticket.flag.mutationOptions({
      meta,
      // Settles once the job's tickets are read again, so the new ticket is on screen before
      // the write lets go. Returning a promise skips the key-level landing hook, so this clears
      // a draft the flag came back to, by the same rule (its ticket id).
      onSuccess: (_data, variables) => {
        void clearLandedDraft('ticket.flag', variables, meta);
        return refreshUntilShown(qc, trpc.ticket.forJob.pathFilter());
      },
    }),
  );
  // This observer's last write, only while it is this draft's.
  const phase =
    flag.variables?.ticketId === draft.ticketId ? mutationState(flag) : 'idle';

  function send() {
    const id = draft.ticketId;
    const title = draft.title.trim();
    if (!id || !title) return;
    const variables = {
      ticketId: id,
      visitId,
      title,
      message: draft.body.trim() || undefined,
      category: kind,
    };
    // Minting the key may throw before any write exists: the lock is released, and the draft
    // and its photos stay as they are.
    const sent = sendOnce(
      lock,
      (v) =>
        flag.mutate(v, {
          onError: () => {
            void giveBackUnlessKept(variables, userId, {
              // The writes of THIS client (the app has one); none known reads as "kept".
              writes: writesFor(qc) ?? { awaits: () => true },
              log: failedWriteLog,
              queue: uploadQueue,
            });
          },
        }),
      variables,
    );
    if (!sent) return; // a second tap, on the flag already sent
    // The flag is on the phone now (its row is written before its first attempt): the form is
    // free for the next problem, and the photos follow this one.
    clearDraft();
    setNextId(crypto.randomUUID());
    setOpen(false);
    void uploadQueue.release('ticket', id);
  }

  function discard() {
    clearDraft();
    setNextId(crypto.randomUUID());
    void uploadQueue.discard('ticket', ticketId);
    setOpen(false);
  }

  const locked = unsent || refused;
  const sendLabel =
    phase === 'queued'
      ? 'Saved — sends when you’re back'
      : phase === 'sending'
        ? 'Sending…'
        : checking
          ? CHECKING_LABEL
          : refused
            ? 'Not sent — see Changes not sent'
            : unsent
              ? 'Saved — sends when you’re back'
              : 'Flag it';

  return (
    <section className="rounded-[var(--radius)] border border-border bg-surface p-3">
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Something wrong?
      </p>
      <Button
        variant="ghost"
        className={`${bigButton} border border-border`}
        onClick={() => setOpen(true)}
      >
        {savedForLater ? 'Problem saved — sends when you’re back' : 'Flag a problem'}
      </Button>

      <Sheet
        open={open}
        onClose={() => setOpen(false)}
        title="Flag a problem"
        footer={
          <div className="grid grid-cols-2 gap-2">
            <Button
              variant="ghost"
              className={bigButton}
              disabled={unsent}
              onClick={discard}
            >
              Discard
            </Button>
            <Button
              className={bigButton}
              disabled={!draft.ticketId || !draft.title.trim() || locked || checking}
              onClick={send}
            >
              {sendLabel}
            </Button>
          </div>
        }
      >
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            The office hears about it straight away, or as soon as you have signal.
          </p>
          {refused ? (
            <Notice tone="danger">
              This problem wasn’t accepted. Open Changes not sent to try it again.
            </Notice>
          ) : null}
          <FlagKindChoice
            name={`flag-kind-${visitId}`}
            value={kind}
            disabled={locked}
            onChange={(category) => setDraft((d) => ({ ...withId(d), category }))}
          />
          <label className="block text-sm font-medium">
            The problem, in a few words
            <input
              className={`${FIELD} mt-1 h-11`}
              value={draft.title}
              maxLength={200}
              disabled={locked}
              onChange={(e) => {
                const title = e.target.value;
                setDraft((d) => ({ ...withId(d), title }));
              }}
              placeholder="Still dripping under the sink"
            />
          </label>
          <label className="block text-sm font-medium">
            What’s wrong (optional)
            <textarea
              className={`${FIELD} mt-1 min-h-[90px] p-2`}
              value={draft.body}
              maxLength={4000}
              disabled={locked}
              onChange={(e) => {
                const body = e.target.value;
                setDraft((d) => ({ ...withId(d), body }));
              }}
              placeholder="What you saw, what the customer said, what it needs"
            />
          </label>
          {/* Parked while the form is being written: nothing exists for them to attach to until
              it is sent. A form whose flag is already on its way or refused sends them
              straight into the queue, held behind the flag. */}
          <PhotoGrid entityType="ticket" entityId={ticketId} parked={!locked} compact />
        </div>
      </Sheet>
    </section>
  );
}

/**
 * The kind of problem, as a segmented choice of four. Native radios in a fieldset: Tab enters
 * the group at the chosen one, the arrow keys move the choice, and the global focus ring shows
 * on the radio itself. The choice is read from the radio's dot, not from the tile's colour.
 * Two by two, so each tile keeps a 44 px target at 390 px.
 */
function FlagKindChoice({
  name,
  value,
  disabled,
  onChange,
}: {
  name: string;
  value: TechFlagKind;
  disabled: boolean;
  onChange: (next: TechFlagKind) => void;
}) {
  return (
    <fieldset className="min-w-0">
      <legend className="mb-1 block text-sm font-medium">What kind of problem</legend>
      <div className="grid grid-cols-2 gap-2">
        {TECH_FLAG_KINDS.map((k) => (
          <label
            key={k}
            className={`flex min-h-11 min-w-0 cursor-pointer items-center gap-2 rounded-[var(--radius)] border px-2.5 py-2 text-sm has-[:disabled]:cursor-default has-[:disabled]:opacity-60 ${
              k === value
                ? 'border-brand bg-brand/10 font-semibold text-text'
                : 'border-border bg-surface text-muted-foreground hover:bg-surface-2 hover:text-text'
            }`}
          >
            <input
              type="radio"
              name={name}
              value={k}
              className="size-4 shrink-0 accent-brand"
              checked={k === value}
              disabled={disabled}
              onChange={() => onChange(k)}
            />
            <span className="min-w-0 leading-snug">{TECH_FLAG_KIND_LABEL[k]}</span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}
